import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/core/utils/cn';
type LabelType = 'normal' | 'required' | 'optional';
type Variant = 'primary' | 'secondary';
export interface CustomSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'placeholder'> {
  label?: string;
  labelType?: LabelType;
  variant?: Variant;
  helperText?: string;
  error?: boolean;
  errorMessage?: string | null;
  labelClassName?: string;
  containerClassName?: string;
  startAdornment?: React.ReactNode;
  options: {
    label: string;
    value: string;
  }[];
  dataPlaceholder?: string;
}
const CustomSelect = React.forwardRef<HTMLSelectElement, CustomSelectProps>(
  (
    {
      label,
      labelType = 'normal',
      variant = 'primary',
      helperText,
      error,
      errorMessage,
      labelClassName,
      containerClassName,
      startAdornment,
      className,
      id,
      options,
      dataPlaceholder,
      value,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const selectId = id ?? generatedId;
    const { t } = useTranslation(['common']);
    const getLabelSuffix = () => {
      if (labelType === 'required') {
        return <span className="ml-1 text-errorDefault">*</span>;
      }
      if (labelType === 'optional') {
        return <span className="ml-1 text-neutralDefault">{t('optional')}</span>;
      }
      return null;
    };
    const variantStyles = {
      primary: 'border-neutralLight bg-neutralWhite text-neutralUltraDark',
      secondary: 'border-neutralExtraLight bg-neutralUltraLight text-neutralDark',
    };
    return (
      <div className={cn('space-y-1', containerClassName)}>
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'bodySm flex items-center capitalize text-neutralUltraDark',
              labelClassName
            )}
          >
            {label}
            {getLabelSuffix()}
          </label>
        )}

        <div className="relative">
          {startAdornment && (
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutralMedium">
              {startAdornment}
            </div>
          )}

          <select
            id={selectId}
            ref={ref}
            value={value}
            className={cn(
              'bodyMd flex h-12 w-full appearance-none rounded-xl border px-3 py-2',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryDefault focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              startAdornment && 'pl-10',
              error && 'border-errorDefault focus-visible:ring-errorDefault',
              variantStyles[variant],
              className
            )}
            {...props}
          >
            {value === '' && (
              <option
                value=""
                disabled
                hidden
              >
                {dataPlaceholder || 'Select...'}
              </option>
            )}
            {options.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>

          {/* ▼ dropdown arrow */}
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutralMedium">
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          </div>
        </div>

        {error && errorMessage ? (
          <p className="bodySm text-errorDefault">{errorMessage}</p>
        ) : helperText ? (
          <p className="bodySm text-neutralDefault">{helperText}</p>
        ) : null}
      </div>
    );
  }
);
CustomSelect.displayName = 'CustomSelect';
export { CustomSelect };
